import React, { useState } from 'react';
import { Container, Typography, Box, Paper, Accordion, AccordionSummary, AccordionDetails, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { DESIGN_TOKENS } from '../theme/designTokens';

const ReportSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
}));

const RiskMatrix = styled(TableContainer)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: '#F9FAFB',
    fontWeight: 600,
  },
  '& .risk-high': {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#991B1B',
    fontWeight: 600,
  },
  '& .risk-medium': {
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    color: '#92400E',
    fontWeight: 600,
  },
  '& .risk-low': {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#065F46',
    fontWeight: 600,
  },
}));

const ReportContents = () => {
  const [expanded, setExpanded] = useState<string | false>('executive');
  const { t } = useTranslation();
  
  // Set page title for analytics
  usePageTitle('Report Contents');

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const riskCategories = t('reportContents.riskCategories.categories', { returnObjects: true }) as Record<string, { name: string, description: string, examples: string[] }>;

  return (
    <ReportSection>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box component="section" role="region" aria-labelledby="report-heading">
            <Typography 
              id="report-heading"
              variant="h1" 
              component="h1" 
              align="center" 
              gutterBottom 
              sx={{ 
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' }, 
                mb: 3, 
                fontWeight: 700 
              }}
            >
              {t('reportContents.title')}
            </Typography>
          </Box>

          <Typography 
            variant="h2" 
            component="h2"
            align="center" 
            paragraph
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              maxWidth: '800px',
              mx: 'auto',
              mb: 6,
              color: '#6B7280',
              lineHeight: 1.6,
            }}
          >
            {t('reportContents.subtitle')}
          </Typography>
        </motion.div>

        <Box sx={{ mb: 6 }}>
          <Accordion 
            expanded={expanded === 'executive'} 
            onChange={handleChange('executive')}
            sx={{ mb: 2, borderRadius: DESIGN_TOKENS.borderRadius.lg, '&:before': { display: 'none' }, border: '1px solid #E5E7EB' }}
            aria-label="Executive Summary Section"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon aria-hidden="true" />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon sx={{ mr: 2, color: '#5963C8' }} aria-hidden="true" />
                <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  {t('reportContents.executiveSummary.title')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#F9FAFB', p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {t('reportContents.executiveSummary.description')}
              </Typography>
              <Box sx={{ pl: 3 }}>
                {(t('reportContents.executiveSummary.points', { returnObjects: true }) as string[]).map((point, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 1.5, display: 'flex' }}><CheckCircleIcon color="success" sx={{mr: 1, fontSize: '1.2rem'}} aria-hidden="true" /> {point}</Typography>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion 
            expanded={expanded === 'detailed'} 
            onChange={handleChange('detailed')}
            sx={{ mb: 2, borderRadius: DESIGN_TOKENS.borderRadius.lg, '&:before': { display: 'none' }, border: '1px solid #E5E7EB' }}
            aria-label="Detailed Risk Observations Section"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon aria-hidden="true" />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon sx={{ mr: 2, color: '#DC2626' }} aria-hidden="true" />
                <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  {t('reportContents.detailedObservations.title')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#F9FAFB', p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {t('reportContents.detailedObservations.description')}
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
                <Paper sx={{ p: 3, bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 2 }}>
                  <Typography variant="h3" component="h3" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2, color: '#991B1B' }}>
                    🔴 {t('reportContents.detailedObservations.highRisk.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{t('reportContents.detailedObservations.highRisk.subtitle')}</Typography>
                  <Box sx={{ pl: 2 }}>
                    {(t('reportContents.detailedObservations.highRisk.points', { returnObjects: true }) as string[]).map((point, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>• {point}</Typography>
                    ))}
                  </Box>
                </Paper>

                <Paper sx={{ p: 3, bgcolor: '#FFFBEB', border: '1px solid #FED7AA', borderRadius: 2 }}>
                  <Typography variant="h3" component="h3" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2, color: '#92400E' }}>
                    🟡 {t('reportContents.detailedObservations.mediumRisk.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{t('reportContents.detailedObservations.mediumRisk.subtitle')}</Typography>
                  <Box sx={{ pl: 2 }}>
                    {(t('reportContents.detailedObservations.mediumRisk.points', { returnObjects: true }) as string[]).map((point, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>• {point}</Typography>
                    ))}
                  </Box>
                </Paper>
              </Box>

              <Paper sx={{ p: 3, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2, mb: 3 }}>
                <Typography variant="h3" component="h3" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2, color: '#166534' }}>
                  🟢 {t('reportContents.detailedObservations.lowRisk.title')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>{t('reportContents.detailedObservations.lowRisk.subtitle')}</Typography>
              </Paper>
            </AccordionDetails>
          </Accordion>

          <Accordion 
            expanded={expanded === 'matrix'} 
            onChange={handleChange('matrix')}
            sx={{ mb: 2, borderRadius: DESIGN_TOKENS.borderRadius.lg, '&:before': { display: 'none' }, border: '1px solid #E5E7EB' }}
            aria-label="Risk Assessment Matrix Section"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon aria-hidden="true" />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 2, color: '#059669' }} aria-hidden="true" />
                <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  {t('reportContents.riskMatrix.title')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#F9FAFB', p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {t('reportContents.riskMatrix.description')}
              </Typography>
              
              <Paper sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
                <RiskMatrix>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('reportContents.riskMatrix.table.score')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.calculation')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.level')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.action')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="risk-high">{t('reportContents.riskMatrix.table.high.score')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.high.calculation')}</TableCell>
                        <TableCell className="risk-high">{t('reportContents.riskMatrix.table.high.level')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.high.action')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="risk-medium">{t('reportContents.riskMatrix.table.medium.score')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.medium.calculation')}</TableCell>
                        <TableCell className="risk-medium">{t('reportContents.riskMatrix.table.medium.level')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.medium.action')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="risk-low">{t('reportContents.riskMatrix.table.low.score')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.low.calculation')}</TableCell>
                        <TableCell className="risk-low">{t('reportContents.riskMatrix.table.low.level')}</TableCell>
                        <TableCell>{t('reportContents.riskMatrix.table.low.action')}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </RiskMatrix>
              </Paper>
            </AccordionDetails>
          </Accordion>

          <Accordion 
            expanded={expanded === 'framework'} 
            onChange={handleChange('framework')}
            sx={{ borderRadius: 3, '&:before': { display: 'none' }, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
            aria-label="4E+HF Risk Category Framework Section"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon aria-hidden="true" />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon sx={{ mr: 2, color: '#8B5CF6' }} aria-hidden="true" />
                <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  {t('reportContents.riskCategories.title')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#F9FAFB', p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {t('reportContents.riskCategories.description')}
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                {Object.values(riskCategories).map((category: { name: string, description: string, examples: string[] }) => (
                  <Paper key={category.name} sx={{ p: 3, borderRadius: 2, border: '1px solid #E5E7EB' }}>
                    <Typography variant="h3" component="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 1 }}>{category.name}</Typography>
                    <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>{category.description}</Typography>
                    <Box>
                      {category.examples.map((example: string) => (
                        <Chip key={example} label={example} size="small" sx={{ mr: 1, mb: 1 }} />
                      ))}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Paper sx={{ p: 4, borderRadius: 3, border: '1px solid #E5E7EB', mb: 6 }}>
          <Typography variant="h2" component="h2" align="center" gutterBottom sx={{ fontSize: '1.5rem', mb: 3, fontWeight: 600 }}>
            {t('reportContents.standards.title')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mb: 3 }}>
            {(t('reportContents.standards.items', { returnObjects: true }) as string[]).map((standard) => (
              <Chip key={standard} label={standard} variant="outlined" color="primary" sx={{fontWeight: 500}} />
            ))}
          </Box>
          <Typography variant="body1" sx={{ color: '#6B7280', lineHeight: 1.6, textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
            {t('reportContents.standards.description')}
          </Typography>
        </Paper>

        <Paper 
          sx={{ 
            p: { xs: 3, sm: 6 }, 
            textAlign: 'center',
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(94, 106, 210, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
            border: '1px solid rgba(94, 106, 210, 0.1)',
          }}
        >
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '2rem', mb: 3, fontWeight: 700 }}>
            {t('reportContents.cta.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7280', maxWidth: '600px', mx: 'auto', mb: 4, lineHeight: 1.6 }}>
            {t('reportContents.cta.description')}
          </Typography>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              variant="contained" 
              size="large"
              component={RouterLink}
              to="/upload"
              sx={{ 
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #5E6AD2 0%, #8B5CF6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4C5BC7 0%, #7C3AED 100%)',
                }
              }}
            >
              {t('reportContents.cta.button')}
            </Button>
          </motion.div>
        </Paper>
      </Container>
    </ReportSection>
  );
};

export default ReportContents; 